import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { Save, Settings2, Server, AlertTriangle, Gauge } from 'lucide-react';
import { useToast } from '../components/ui/toastContext';

interface SystemSettings {
  platform_name: string;
  default_language: string;
  allow_registration: boolean;
}

interface SystemConfig {
  autoscale_enabled: boolean;
  autoscale_cpu_threshold: number;
  autoscale_memory_threshold: number;
  autoscale_occupancy_threshold: number;
  autoscale_cooldown_minutes: number;
  autoshrink_enabled: boolean;
  autoshrink_cpu_threshold: number;
  autoshrink_memory_threshold: number;
  autoshrink_min_idle_minutes: number;
  container_alert_cpu_threshold: number;
  container_alert_memory_threshold: number;
  container_alert_consecutive_count: number;
}

const DEFAULT_SETTINGS: SystemSettings = {
  platform_name: 'Atelier 琢玉坊',
  default_language: 'zh',
  allow_registration: false,
};

const DEFAULT_SYSTEM_CONFIG: SystemConfig = {
  autoscale_enabled: false,
  autoscale_cpu_threshold: 80,
  autoscale_memory_threshold: 80,
  autoscale_occupancy_threshold: 0.8,
  autoscale_cooldown_minutes: 5,
  autoshrink_enabled: false,
  autoshrink_cpu_threshold: 10,
  autoshrink_memory_threshold: 20,
  autoshrink_min_idle_minutes: 30,
  container_alert_cpu_threshold: 80,
  container_alert_memory_threshold: 80,
  container_alert_consecutive_count: 3,
};

export default function Settings() {
  const toast = useToast();
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [sysConfigLoading, setSysConfigLoading] = useState(true);
  const [sysSaving, setSysSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get('/configs/resolved?key=platform_settings')
      .then((res) => {
        if (res.data.value) {
          setSettings({ ...DEFAULT_SETTINGS, ...res.data.value });
        }
      })
      .catch((e) => console.error('Failed to load settings:', e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    apiClient
      .get('/system-config')
      .then((res) => {
        setSystemConfig(res.data);
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          setSystemConfig(null);
        } else {
          console.error('Failed to load system config:', e);
        }
      })
      .finally(() => setSysConfigLoading(false));
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put('/configs', {
        key: 'platform_settings',
        value: settings,
        scope: 'system',
        description: '平台全局设置',
      });
      toast.success('全局设置已保存');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      toast.error('保存失败: ' + (err.response?.data?.detail || err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  const saveSystemConfig = async () => {
    if (!systemConfig) return;
    setSysSaving(true);
    try {
      const res = await apiClient.put('/system-config', { values: systemConfig });
      setSystemConfig(res.data);
      toast.success('系统动态配置已保存');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      toast.error('保存失败: ' + (err.response?.data?.detail || err.message || '未知错误'));
    } finally {
      setSysSaving(false);
    }
  };

  const updateNumber = (
    key: keyof SystemConfig,
    value: string,
    min?: number,
    max?: number
  ) => {
    if (systemConfig === null) return;
    let num = value === '' ? 0 : Number(value);
    if (min !== undefined && num < min) num = min;
    if (max !== undefined && num > max) num = max;
    setSystemConfig({ ...systemConfig, [key]: num });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>
        <Settings2 size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />
        系统设置
      </h1>

      <div className="card mt-4" style={{ maxWidth: 600 }}>
        <h3 className="mb-3">全局设置</h3>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
              平台名称
            </label>
            <input
              value={settings.platform_name}
              onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
              默认语言
            </label>
            <select
              value={settings.default_language}
              onChange={(e) => setSettings({ ...settings, default_language: e.target.value })}
            >
              <option value="zh">中文</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="allow_registration"
              type="checkbox"
              checked={settings.allow_registration}
              onChange={(e) => setSettings({ ...settings, allow_registration: e.target.checked })}
            />
            <label htmlFor="allow_registration" className="text-sm">
              允许开放注册
            </label>
          </div>
          <div className="flex gap-2 mt-2">
            <button className="primary" onClick={saveSettings} disabled={saving}>
              <Save size={14} /> {saving ? '保存中...' : '保存设置'}
            </button>
            <button className="secondary" onClick={() => setSettings(DEFAULT_SETTINGS)}>
              重置默认值
            </button>
          </div>
        </div>
      </div>

      {!sysConfigLoading && systemConfig !== null && (
        <div className="card mt-4" style={{ maxWidth: 800 }}>
          <h3 className="mb-3">
            <Server size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            系统动态配置
            <span className="text-sm text-muted" style={{ marginLeft: 12, fontWeight: 'normal' }}>
              仅 Super Admin 可编辑
            </span>
          </h3>

          <div className="flex flex-col gap-4">
            <section>
              <h4 className="mb-2">
                <Gauge size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                自动扩容
              </h4>
              <div className="grid grid-cols-2 gap-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                <div className="flex items-center gap-2">
                  <input
                    id="autoscale_enabled"
                    type="checkbox"
                    checked={systemConfig.autoscale_enabled}
                    onChange={(e) =>
                      setSystemConfig({ ...systemConfig, autoscale_enabled: e.target.checked })
                    }
                  />
                  <label htmlFor="autoscale_enabled" className="text-sm">
                    启用自动扩容
                  </label>
                </div>
                <NumberField
                  label="CPU 阈值 (%)"
                  value={systemConfig.autoscale_cpu_threshold}
                  onChange={(v) => updateNumber('autoscale_cpu_threshold', v, 0, 100)}
                />
                <NumberField
                  label="内存阈值 (%)"
                  value={systemConfig.autoscale_memory_threshold}
                  onChange={(v) => updateNumber('autoscale_memory_threshold', v, 0, 100)}
                />
                <NumberField
                  label="容量阈值 (0-1)"
                  value={systemConfig.autoscale_occupancy_threshold}
                  step={0.05}
                  onChange={(v) => updateNumber('autoscale_occupancy_threshold', v, 0, 1)}
                />
                <NumberField
                  label="冷却期 (分钟)"
                  value={systemConfig.autoscale_cooldown_minutes}
                  onChange={(v) => updateNumber('autoscale_cooldown_minutes', v, 0)}
                />
              </div>
            </section>

            <section>
              <h4 className="mb-2">
                <Gauge size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                自动缩容
              </h4>
              <div className="grid grid-cols-2 gap-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                <div className="flex items-center gap-2">
                  <input
                    id="autoshrink_enabled"
                    type="checkbox"
                    checked={systemConfig.autoshrink_enabled}
                    onChange={(e) =>
                      setSystemConfig({ ...systemConfig, autoshrink_enabled: e.target.checked })
                    }
                  />
                  <label htmlFor="autoshrink_enabled" className="text-sm">
                    启用自动缩容
                  </label>
                </div>
                <NumberField
                  label="CPU 阈值 (%)"
                  value={systemConfig.autoshrink_cpu_threshold}
                  onChange={(v) => updateNumber('autoshrink_cpu_threshold', v, 0, 100)}
                />
                <NumberField
                  label="内存阈值 (%)"
                  value={systemConfig.autoshrink_memory_threshold}
                  onChange={(v) => updateNumber('autoshrink_memory_threshold', v, 0, 100)}
                />
                <NumberField
                  label="最小空闲时间 (分钟)"
                  value={systemConfig.autoshrink_min_idle_minutes}
                  onChange={(v) => updateNumber('autoshrink_min_idle_minutes', v, 0)}
                />
              </div>
            </section>

            <section>
              <h4 className="mb-2">
                <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                容器告警阈值
              </h4>
              <div className="grid grid-cols-2 gap-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                <NumberField
                  label="CPU 阈值 (%)"
                  value={systemConfig.container_alert_cpu_threshold}
                  onChange={(v) => updateNumber('container_alert_cpu_threshold', v, 0, 100)}
                />
                <NumberField
                  label="内存阈值 (%)"
                  value={systemConfig.container_alert_memory_threshold}
                  onChange={(v) => updateNumber('container_alert_memory_threshold', v, 0, 100)}
                />
                <NumberField
                  label="连续次数"
                  value={systemConfig.container_alert_consecutive_count}
                  onChange={(v) => updateNumber('container_alert_consecutive_count', v, 1)}
                />
              </div>
            </section>

            <div className="flex gap-2 mt-2">
              <button className="primary" onClick={saveSystemConfig} disabled={sysSaving}>
                <Save size={14} /> {sysSaving ? '保存中...' : '保存系统配置'}
              </button>
              <button
                className="secondary"
                onClick={() => setSystemConfig(DEFAULT_SYSTEM_CONFIG)}
              >
                重置默认值
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      <input
        type="number"
        step={step ?? 1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
